import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import IntroductionSection from './components/IntroductionSection';
import SkillsSection from './components/SkillsSection';
import ExperienceTimeline from './components/ExperienceTimeline';
import ExpertiseSection from './components/ExpertiseSection';
import ResumeDownload from './components/ResumeDownload';
import type { AboutData } from './types';

const AboutSection = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const aboutData: AboutData = {
    introduction: t("about.introduction"),
    mission: t("about.mission"),
    skills: [
      { name: "Laravel / PHP", level: 95, category: t("about.categories.backend") },
      { name: "Node.js", level: 85, category: t("about.categories.backend") },
      { name: "MySQL / MongoDB", level: 90, category: t("about.categories.database") },
      { name: "React / Next.js", level: 93, category: t("about.categories.frontend") },
      { name: "TypeScript", level: 90, category: t("about.categories.frontend") },
      { name: "Tailwind CSS", level: 90, category: t("about.categories.frontend") },
      { name: "React Native / Expo", level: 75, category: t("about.categories.mobile") },
      { name: "Docker", level: 82, category: t("about.categories.devops") },
      { name: "System Architecture", level: 90, category: t("about.categories.architecture") },
      { name: "API Design", level: 92, category: t("about.categories.architecture") },
      { name: "AI APIs (Claude, DeepSeek)", level: 90, category: t("about.categories.ai") },
      { name: "Building AI Agents", level: 88, category: t("about.categories.ai") },
      { name: "CLI & AI Tooling", level: 85, category: t("about.categories.ai") }
    ],
    experiences: [
      {
        year: "Aug 2024 - May 2025",
        title: t("about.experiences.walaa.title"),
        company: "WalaaGroup (Remote)",
        description: t("about.experiences.walaa.desc"),
        technologies: ["Laravel", "Inertia", "MySQL", "React.js", "TailwindCSS", "TypeScript", "Redux", "Breeze", "PDFdom"]
      },
      {
        year: "Dec 2024 - Feb 2025",
        title: t("about.experiences.moj.title"),
        company: "MOJ (Remote)",
        description: t("about.experiences.moj.desc"),
        technologies: ["Laravel", "MySQL", "jQuery", "Bootstrap", "Google Analytics"]
      },
      {
        year: "Aug 2024 - Nov 2024",
        title: t("about.experiences.dropidea.title"),
        company: "Dropidea Digital (Remote)",
        description: t("about.experiences.dropidea.desc"),
        technologies: ["React", "Next.js", "TailwindCSS", "TypeScript", "Figma"]
      },
      {
        year: "May 2019 - May 2024",
        title: t("about.experiences.freelance.title"),
        company: "Freelance (Remote)",
        description: t("about.experiences.freelance.desc"),
        technologies: ["Laravel", "Inertia", "MySQL", "React.js", "TailwindCSS", "TypeScript", "Redux", "HTML5"]
      }
    ],
    expertiseAreas: [
      {
        icon: "Code2",
        title: t("about.expertise.fullstack.title"),
        description: t("about.expertise.fullstack.desc"),
        skills: t("about.expertise.fullstack.skills", { returnObjects: true }) as string[]
      },
      {
        icon: "Database",
        title: t("about.expertise.architecture.title"),
        description: t("about.expertise.architecture.desc"),
        skills: t("about.expertise.architecture.skills", { returnObjects: true }) as string[]
      },
      {
        icon: "ShoppingCart",
        title: t("about.expertise.ecommerce.title"),
        description: t("about.expertise.ecommerce.desc"),
        skills: t("about.expertise.ecommerce.skills", { returnObjects: true }) as string[]
      },
      {
        icon: "Cloud",
        title: t("about.expertise.cloud.title"),
        description: t("about.expertise.cloud.desc"),
        skills: t("about.expertise.cloud.skills", { returnObjects: true }) as string[]
      },
      {
        icon: "Shield",
        title: t("about.expertise.security.title"),
        description: t("about.expertise.security.desc"),
        skills: t("about.expertise.security.skills", { returnObjects: true }) as string[]
      },
      {
        icon: "Bot",
        title: t("about.expertise.ai.title"),
        description: t("about.expertise.ai.desc"),
        skills: t("about.expertise.ai.skills", { returnObjects: true }) as string[]
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{t("about.title")}</title>
        <meta
          name="description"
          content={t("about.descriptionMeta")}
        />
        <meta
          name="keywords"
          content={t("about.keywordsMeta")}
        />
      </Helmet>

      <div className="min-h-screen bg-background gradient-background">
        <Header />
        
        <main className="pt-24 pb-16 px-4 md:px-6 lg:px-8">
          <IntroductionSection
            introduction={aboutData.introduction}
            mission={aboutData.mission}
          />
          
          <SkillsSection skills={aboutData.skills} />
          
          <ExperienceTimeline experiences={aboutData.experiences} />
          
          <ExpertiseSection expertiseAreas={aboutData.expertiseAreas} />
          
          <ResumeDownload />
        </main>
      </div>
    </>
  );
};

export default AboutSection;